import { useQuery } from '@tanstack/react-query'
import { DataTable, useDataTableContext } from 'components/DataTable'
import { IColumnSort } from 'components/DataTable/types'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import {
  CreateContactRequestJobModal,
  MAX_OPPORTUNITIES_REQUESTED,
} from 'components/Modals/CreateContactRequestJobModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import React, { useEffect, useMemo, useState } from 'react'
import {
  BsBriefcase,
  BsDownload,
  BsPhone,
  BsRocketTakeoff,
  BsTags,
  BsTruck,
  BsXCircle,
} from 'react-icons/bs'
import styled from 'styled-components'
import { featureFlagService } from 'utils/featureFlagService'
import { AssignToCampaignModal } from '../../../../features/campaigns/AssignToCampaignModal'
import { useCampaignUnassigner } from '../../../../features/campaigns/useCampaignUnassigner'
import { FilterIdentifier } from '../../../../models/saved_view'
import { getTableQueryKey } from '../../../../utils/getTableQueryKey'
import TableButtonAction from '../../../Buttons/TableButtons/TableButtonAction'
import { CreateDealModalForTable } from '../../../Deals/CreateDealModal/CreateDealModal'

import { TableSearch } from '../../../Filters/TableSearch'
import DistributorAssignModal from '../../../Modals/AssignmentModal/DistributorAssignModal'
import GPOAssignModal from '../../../Modals/AssignmentModal/GPOAssignModal'
import TagAssignModal from '../../../Modals/AssignmentModal/TagAssignModal'
import { IDropdownItem } from '../../../UI/Dropdown/Dropdown'
import * as S from './CommonTable.styles'

import { ColumnDef } from '@tanstack/react-table'
import { ExportModalProps } from 'components/Modals/ExportModal/ExportModal'
import {
  StandardPaginatedResponse,
  StandardSummaryResponse,
} from 'models/paginated-response'
import {
  ALLOWED_CONTACT_REQUEST_TYPES,
  CompanyTypeSlug,
  TableTypeSlug,
} from 'models/companies'
import { dataTableSortingStoreRepo } from '../../../DataTable/DataTableSorting/DataTableSortingStore'
import { cn } from '../../../UI/cn'
import apiService from 'services/api'
import { SiSalesforce } from 'react-icons/si'
import { toast } from 'react-toastify'
import { PiShippingContainer, PiCalendarStar } from 'react-icons/pi'
import { ReminderModalForTable } from 'components/Reminders/ReminderModal'

const PAGE_SIZE = 100

type TableFeatures = {
  pushToSalesforce?: boolean
  contactRequests?: boolean
  campaigns?: boolean
  createDeals?: boolean
  manageTags?: boolean
  manageDistributors?: boolean
  manageGPOs?: boolean
  exportSelected?: boolean
}

/**
 * Props for the Data Expansion Common Table component
 * @template T - The type of data items in the table, must include an id property of type number
 */
export interface DECommonTableProps<T extends { id: number }> {
  setTotalRowsCount?: (count: StandardSummaryResponse) => void
  pageParamName?: string
  baseFilters?: Record<string, any>
  tableKey: string
  filterIdentifier: FilterIdentifier
  filterIdentifierModifier?: FilterIdentifier
  companyTypeSlug: CompanyTypeSlug
  tableTypeSlug: TableTypeSlug
  sortableFields?: string[]
  columns: ColumnDef<T, any>[]
  defaultSort?: IColumnSort<object>[]
  searchPlaceholder?: string
  hideCreateCompanyButton?: boolean
  apiGetListAction: (
    params: Record<string, any>
  ) => Promise<StandardPaginatedResponse<T>>
  apiGetSummaryAction: (
    params: Record<string, any>
  ) => Promise<{ count: number; alt_count: number }>
  FilterSet: React.FC<{
    filterIdentifier: FilterIdentifier
    hideCampaignsFilter?: boolean
  }>
  ExportModal: React.FC<ExportModalProps>
  AfterSearchComponent?: React.ReactNode
  /**
   * Path to find the chain proxy id from the item in the dataset.
   * E.g. for object { name: "", chain_proxy: { id: 123 }}, the prop should be set to "chain_proxy.id".
   * Defaults to "id".
   */
  chainProxyIdAccessorKey?: string
  /**
   * Path to find the door id from the item in the dataset.
   * E.g. for object { name: "", door: { id: 123 }}, the prop should be set to "door.id".
   * Defaults to "id".
   */
  doorProxyIdAccessorKey?: string
  /**
   * Whether the table is a doors table.
   */
  isDoorsTable?: boolean
  /**
   * Features that are enabled for the table.
   * By default, all features are enabled.
   * Passing [feature]: false will disable the feature.
   */
  tableFeatures?: TableFeatures
}

export function DECommonTable<T extends { id: number }>(
  props: DECommonTableProps<T>
) {
  const api = apiService()
  const featureFlag = featureFlagService()

  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()

  const getFeatureEnabled = (feature: keyof TableFeatures) => {
    return Boolean(props.tableFeatures?.[feature] ?? true)
  }

  const {
    state: { rowSelection, isAllRowsSelected, totalSelectedRows },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()

  const hasCampaign = !!(
    props.baseFilters?.main_campaign ?? props.baseFilters?.campaigns
  )
  const IDENTIFIER =
    props.filterIdentifier + (props.filterIdentifierModifier ?? '')
  const filterStore = filterStoreRepo.getStore(IDENTIFIER as FilterIdentifier)
  const { page, setPage } = filterStore()

  const filterParams = {
    ...useFilterParams(filterStore),
    ...props.baseFilters,
  }

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const totalRowsSelected = useMemo(() => {
    return isAllRowsSelected
      ? totalSelectedRows
      : Object.keys(rowSelection).length
  }, [isAllRowsSelected, rowSelection])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  const SUMMARY_QUERY_KEY = [...TABLE_QUERY_KEY, 'summary']

  // DATA FETCHING - Main list
  const { isFetching, data, refetch } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    queryFn: async () => {
      clearSelectedRows()

      const res = await props.apiGetListAction({
        page: page,
        limit: PAGE_SIZE,
        ...filterAndSortParams,
      })

      return res
    },
  })

  // DATA FETCHING - Summary data (separate from main table loading)
  const { data: summaryData } = useQuery({
    staleTime: Infinity,
    queryKey: SUMMARY_QUERY_KEY,
    queryFn: async () => {
      const summaryRes = await props.apiGetSummaryAction({
        ...filterAndSortParams,
      })

      const finalData = {
        count: summaryRes.count,
        alt_count: summaryRes.alt_count,
      }

      props.setTotalRowsCount?.(finalData)
      setTotalRowsInBackend(
        props.isDoorsTable ? summaryRes.alt_count : summaryRes.count
      )

      return finalData
    },
  })
  // END DATA FETCHING

  const unassignFromCampaign = useCampaignUnassigner(props.companyTypeSlug, {
    data: data?.results ?? [],
    accessorKey: 'id',
    tableQueryKey: TABLE_QUERY_KEY,
    requestParams: filterAndSortParams,
    campaignId:
      props?.baseFilters?.main_campaign ?? props?.baseFilters?.campaigns,
    setPage: setPage,
    totalCount: summaryData?.count ?? 0,
    pageSize: PAGE_SIZE,
  })

  useEffect(() => {
    clearSelectedRows()
  }, [data])

  const selectedIds = useMemo(() => {
    return (
      data?.results
        ?.filter((_: any, i: number) => rowSelection[i])
        ?.map((r: any) => r.id) ?? []
    )
  }, [data, rowSelection])

  const unselectedIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      data?.results?.filter((_, i) => !rowSelection[i])?.map((r) => r.id) ?? []
    )
  }, [data, rowSelection, isAllRowsSelected])

  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [openDealModal, setOpenDealModal] = useState(false)
  const [showContactRequest, setShowContactRequest] = useState(false)
  const [showExportAllModal, setShowExportAllModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const handleCreateDeals = () => {
    if (!data) return
    setOpenDealModal(true)
  }

  const columns = props.columns

  useEffect(() => {
    if (summaryData) {
      props.setTotalRowsCount?.(summaryData)
      setTotalRowsInBackend?.(
        props.isDoorsTable ? summaryData.alt_count : summaryData.count
      )
    }
  }, [summaryData])

  const actions = useMemo(() => {
    const baseActions: IDropdownItem[] = [
      {
        label: 'Create Deals',
        icon: <BsBriefcase size={20} />,
        callback: handleCreateDeals,
      },
      {
        label: 'Manage Tags',
        icon: <BsTags size={20} />,
        callback: () => setShowAssignTags(true),
        hidden: !getFeatureEnabled('manageTags'),
      },
      {
        label: 'Add Known Distributors',
        icon: <BsTruck size={20} />,
        callback: () => setShowAssignDistributors(true),
        hidden: !getFeatureEnabled('manageDistributors'),
      },
      {
        label: 'Add GPO',
        icon: <PiShippingContainer size={20} />,
        callback: () => setShowAssignGPOs(true),
        hidden: !getFeatureEnabled('manageGPOs'),
      },
      ...(featureFlag.enableReminders
        ? [
            {
              label: 'Schedule Reminder',
              icon: <PiCalendarStar size={20} />,
              callback: () => setShowReminderModal(true),
              disabled: selectedIds.length !== 1,
              disabledTooltipText: props.isDoorsTable
                ? 'Please select exactly one door to schedule a reminder'
                : 'Please select exactly one company to schedule a reminder',
            },
          ]
        : []),
      {
        label: 'Export Selected',
        icon: <BsDownload size={20} />,
        callback: () => setShowExportAllModal(true),
        hidden: !getFeatureEnabled('exportSelected'),
      },
    ]

    const finalActions: IDropdownItem[] = [...baseActions]

    if (!props.isDoorsTable) {
      finalActions.push({
        label: 'Push to Salesforce',
        icon: <SiSalesforce size={20} />,
        callback: () => {
          setTimeout(() => {
            toast.success('Successfully pushed to Salesforce')
          }, 1000)
        },
        hidden:
          !featureFlag.enablePushToSalesforce ||
          !getFeatureEnabled('pushToSalesforce'),
      })
    }

    // Check if contact requests are allowed for this company type
    const isContactRequestAllowed =
      props.companyTypeSlug &&
      ALLOWED_CONTACT_REQUEST_TYPES.includes(props.companyTypeSlug as any)

    if (featureFlag.enableContactRequests && !props.isDoorsTable) {
      const isDisabledDueToSelection =
        totalRowsSelected > MAX_OPPORTUNITIES_REQUESTED
      const isDisabledDueToCompanyType = !isContactRequestAllowed

      finalActions.push({
        label: 'Request Contacts',
        icon: <BsPhone size={20} />,
        callback: () => setShowContactRequest(true),
        disabled: isDisabledDueToSelection || isDisabledDueToCompanyType,
        disabledTooltipText: isDisabledDueToCompanyType
          ? "Contact requests are not available for companies outside First Bite's database."
          : `To request contacts, select less than ${MAX_OPPORTUNITIES_REQUESTED} opportunities.`,
        hidden: !getFeatureEnabled('contactRequests'),
      })
    }

    if (featureFlag.enableCampaigns && !props.isDoorsTable) {
      if (props.isDoorsTable) {
        finalActions.push({
          label: 'Apply to selected companies',
          isGroupHeader: true,
        })
      }
      if (!hasCampaign) {
        finalActions.push({
          label: 'Push to Campaign',
          icon: <BsRocketTakeoff size={20} />,
          subContent: (
            <AssignToCampaignModal
              data={data?.results ?? []}
              accessorKey={props.chainProxyIdAccessorKey ?? 'id'}
              tableQueryKey={TABLE_QUERY_KEY}
              requestParams={filterAndSortParams}
              companyTypeSlug={props.companyTypeSlug}
              tableTypeSlug={props.companyTypeSlug}
              buttonLess
            />
          ),
          hidden: !getFeatureEnabled('campaigns'),
        })
      } else {
        finalActions.push({
          label: 'Unassign from Campaign',
          icon: <BsXCircle size={20} />,
          callback: () => unassignFromCampaign.unassign(),
          hidden: !getFeatureEnabled('campaigns'),
        })
      }
    }

    return finalActions
  }, [
    data,
    hasCampaign,
    featureFlag,
    props.isDoorsTable,
    totalSelectedRows,
    unassignFromCampaign,
    props.doorProxyIdAccessorKey,
    props.chainProxyIdAccessorKey,
    TABLE_QUERY_KEY,
    filterAndSortParams,
    props.companyTypeSlug,
  ])

  return (
    <TableContainer>
      <CreateContactRequestJobModal
        data={data?.results ?? []}
        open={showContactRequest}
        onClose={() => setShowContactRequest(false)}
        // Note: we door tables do not support contact requests
        accessorKeys={[props.chainProxyIdAccessorKey ?? 'id']}
        tableQueryKey={TABLE_QUERY_KEY}
        requestParams={filterAndSortParams}
        companyTypeSlug={props.companyTypeSlug}
      />

      <TagAssignModal
        show={showAssignTags}
        handleClose={() => setShowAssignTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) =>
          api.commonManageTags({
            values,
            tableType: props.tableTypeSlug,
          })
        }
        idAccessor={
          props.doorProxyIdAccessorKey ?? props.chainProxyIdAccessorKey ?? 'id'
        }
      />

      <DistributorAssignModal
        show={showAssignDistributors}
        handleClose={() => setShowAssignDistributors(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor={
          props.doorProxyIdAccessorKey ?? props.chainProxyIdAccessorKey ?? 'id'
        }
        filterAndSortParams={filterAndSortParams}
        apiDistributorsAction={(values) =>
          api.commonManageDistributors({
            tableType: props.tableTypeSlug,
            values,
          })
        }
      />

      <GPOAssignModal
        show={showAssignGPOs}
        handleClose={() => setShowAssignGPOs(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor={
          props.doorProxyIdAccessorKey ?? props.chainProxyIdAccessorKey ?? 'id'
        }
        filterAndSortParams={filterAndSortParams}
        apiGPOsAction={(values) =>
          api.commonManageGPOs({
            tableType: props.tableTypeSlug,
            values,
          })
        }
      />

      <props.ExportModal
        show={showExportAllModal}
        _handleClose={() => {
          setShowExportAllModal(false)
        }}
        filters={filterAndSortParams}
        selectedIds={!isAllRowsSelected ? selectedIds : undefined}
        excludeIds={isAllRowsSelected ? unselectedIds : undefined}
        count={selectedIds.length || summaryData?.count || 0}
        tableTypeSlug={props.tableTypeSlug}
      />

      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full gap-2 items-center">
              <TableSearch
                filterStore={filterStore}
                searchPlaceholder={
                  props.searchPlaceholder ?? 'Search Opportunities'
                }
                className="flex-1"
                tableKey={props.tableKey}
              />

              {props.AfterSearchComponent}

              <props.FilterSet
                filterIdentifier={IDENTIFIER as FilterIdentifier}
                hideCampaignsFilter={
                  props.baseFilters?.main_campaign ??
                  props.baseFilters?.campaigns
                }
              />

              <ColumnSelectorRecipient tableKey={props.tableKey} />
            </div>

            <div className={cn('flex flex-row justify-between gap-4')}>
              <TableButtonAction
                items={actions}
                disabled={!totalSelectedRows}
                selectedRowsCount={totalSelectedRows ?? 0}
                isDoorsTable={props.isDoorsTable}
              />
              <FilterChips
                identifier={IDENTIFIER as FilterIdentifier}
                clearAllButton
                showActive
                store={filterStore}
              />
            </div>
          </S.SearchContainer>
        }
        tableKey={props.tableKey}
        loading={isFetching || unassignFromCampaign.isUnassigning}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={props.sortableFields}
        virtualizeRows={true}
        enableRowSelection
        isPaginationEnabled={true}
        defaultSort={props.defaultSort}
        paginationOptions={{
          totalRows: props.isDoorsTable
            ? (summaryData?.alt_count ?? 0)
            : (summaryData?.count ?? 0),
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText="Select Opportunities"
        footerControls={
          <OpportunitiesTableFooterControls
            hideCreateCompanyButton={props.hideCreateCompanyButton}
            companyTypeSlug={props.companyTypeSlug}
            onDownloadButtonClick={() => setShowExportAllModal(true)}
          />
        }
      />

      <CreateDealModalForTable
        optimisticUiTarget="none"
        show={openDealModal}
        handleClose={() => setOpenDealModal(false)}
        onDealCreated={refetch}
        queryKey={TABLE_QUERY_KEY}
        filterAndSortParams={filterAndSortParams}
        data={data?.results ?? []}
        idAccessor={props.chainProxyIdAccessorKey ?? 'id'}
        originCampaignId={
          props.baseFilters?.main_campaign ?? props.baseFilters?.campaigns
        }
        tableTypeSlug={props.tableTypeSlug}
        companyTypeSlug={props.companyTypeSlug}
        forDoorSelection={props.isDoorsTable}
      />

      {featureFlag.enableReminders && (
        <ReminderModalForTable
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          data={data?.results}
          isDoorsTable={props.isDoorsTable}
          chainProxyIdAccessorKey={props.chainProxyIdAccessorKey}
          doorProxyIdAccessorKey={props.doorProxyIdAccessorKey}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}
    </TableContainer>
  )
}

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
`
